import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Chip,
  Stack
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { eventsService } from '../../services/api';

const EventForm = ({ event, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    theme: '',
    start_date: null,
    end_date: null,
    status: 'pending',
    max_participants: '',
    submission_guidelines: '',
    min_word_count: '',
    max_word_count: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Common themes for quick selection
  const commonThemes = [
    'Mystery',
    'Romance',
    'Science Fiction',
    'Fantasy',
    'Horror',
    'Adventure',
    'Drama',
    'Comedy',
    'Historical Fiction',
    'Thriller',
    'Coming of Age',
    'Family',
    'Friendship',
    'Love',
    'Loss',
    'Hope',
    'Redemption',
    'Journey',
    'Discovery',
    'Transformation'
  ];

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title || '',
        description: event.description || '',
        theme: event.theme || '',
        start_date: event.start_date ? new Date(event.start_date) : null,
        end_date: event.end_date ? new Date(event.end_date) : null,
        status: event.status || 'pending',
        max_participants: event.max_participants || '',
        submission_guidelines: event.submission_guidelines || '',
        min_word_count: event.min_word_count || '',
        max_word_count: event.max_word_count || ''
      });
    }
  }, [event]);

  const handleChange = (field) => (value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleThemeSelect = (theme) => {
    setFormData(prev => ({
      ...prev,
      theme: theme
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    }

    if (formData.start_date && formData.end_date) {
      if (formData.start_date >= formData.end_date) {
        newErrors.end_date = 'End date must be after start date';
      }

      // Check if start date is in the past (only for new events)
      // Allow events to start today by comparing dates only (not time)
      if (!event) {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to beginning of today
        const startDate = new Date(formData.start_date);
        startDate.setHours(0, 0, 0, 0); // Set to beginning of selected date
        
        if (startDate < today) {
          newErrors.start_date = 'Start date cannot be in the past';
        }
      }
    }

    if (formData.max_participants && (isNaN(formData.max_participants) || formData.max_participants < 1)) {
      newErrors.max_participants = 'Max participants must be a positive number';
    }

    // Validate word count limits
    if (formData.min_word_count && (isNaN(formData.min_word_count) || formData.min_word_count < 1)) {
      newErrors.min_word_count = 'Minimum word count must be a positive number';
    }

    if (formData.max_word_count && (isNaN(formData.max_word_count) || formData.max_word_count < 1)) {
      newErrors.max_word_count = 'Maximum word count must be a positive number';
    }

    if (formData.min_word_count && formData.max_word_count) {
      const minWords = parseInt(formData.min_word_count);
      const maxWords = parseInt(formData.max_word_count);
      if (minWords > maxWords) {
        newErrors.max_word_count = 'Maximum word count must be greater than minimum word count';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const eventData = {
        ...formData,
        start_date: formData.start_date.toISOString().split('T')[0],
        end_date: formData.end_date.toISOString().split('T')[0],
        max_participants: formData.max_participants ? parseInt(formData.max_participants) : null,
        min_word_count: formData.min_word_count ? parseInt(formData.min_word_count) : null,
        max_word_count: formData.max_word_count ? parseInt(formData.max_word_count) : null
      };

      if (event) {
        await eventsService.updateEvent(event.event_id, eventData);
      } else {
        await eventsService.createEvent(eventData);
      }

      onSave();
    } catch (err) {
      console.error('Error saving event:', err);
      setErrors({ submit: 'Failed to save event. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        {errors.submit && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.submit}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Title */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Event Title"
              value={formData.title}
              onChange={(e) => handleChange('title')(e.target.value)}
              error={!!errors.title}
              helperText={errors.title}
              required
            />
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) => handleChange('description')(e.target.value)}
              error={!!errors.description}
              helperText={errors.description}
              required
            />
          </Grid>

          {/* Theme */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Theme (Optional)"
              value={formData.theme}
              onChange={(e) => handleChange('theme')(e.target.value)}
              helperText="Enter a custom theme or select from common themes below"
            />
            
            {/* Common Themes */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Quick Select Themes:
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                {commonThemes.map((theme) => (
                  <Chip
                    key={theme}
                    label={theme}
                    onClick={() => handleThemeSelect(theme)}
                    variant={formData.theme === theme ? 'filled' : 'outlined'}
                    color={formData.theme === theme ? 'primary' : 'default'}
                    size="small"
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Stack>
            </Box>
          </Grid>

          {/* Dates */}
          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Start Date"
              value={formData.start_date}
              onChange={handleChange('start_date')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!errors.start_date}
                  helperText={errors.start_date}
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DatePicker
              label="End Date"
              value={formData.end_date}
              onChange={handleChange('end_date')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!errors.end_date}
                  helperText={errors.end_date}
                  required
                />
              )}
            />
          </Grid>

          {/* Status (only for editing) */}
          {event && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => handleChange('status')(e.target.value)}
                >
                  <MenuItem value="upcoming">Upcoming</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Max Participants */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Max Participants (Optional)"
              type="number"
              value={formData.max_participants}
              onChange={(e) => handleChange('max_participants')(e.target.value)}
              error={!!errors.max_participants}
              helperText={errors.max_participants || 'Leave empty for unlimited'}
              inputProps={{ min: 1 }}
            />
          </Grid>

          {/* Word Count Limits */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Word Count Requirements (Optional)
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Minimum Word Count"
              type="number"
              value={formData.min_word_count}
              onChange={(e) => handleChange('min_word_count')(e.target.value)}
              error={!!errors.min_word_count}
              helperText={errors.min_word_count || 'Leave empty for no minimum'}
              inputProps={{ min: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Maximum Word Count"
              type="number"
              value={formData.max_word_count}
              onChange={(e) => handleChange('max_word_count')(e.target.value)}
              error={!!errors.max_word_count}
              helperText={errors.max_word_count || 'Leave empty for no maximum'}
              inputProps={{ min: 1 }}
            />
          </Grid>

          {/* Submission Guidelines */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Submission Guidelines (Optional)"
              multiline
              rows={3}
              value={formData.submission_guidelines}
              onChange={(e) => handleChange('submission_guidelines')(e.target.value)}
              helperText="Provide guidelines for story submissions"
            />
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : (event ? 'Update Event' : 'Create Event')}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default EventForm;
